<template>
  <simulador-card
    :title="$t('tools.viabilidadeTitle')"
    :info="$t('tools.viabilidadeInfo')"
  >
    <div class="margin-container">
      <simulador-field
        id="margem-contribuicao"
        :label="$t('tools.margemContribuicaoLabel')"
        :value="formatarValor(totalContasVariaveis)"
        unit="%"
        :formula="$t('tools.margemContribuicaoFormula')"
        :is-result="true"
      />

      <simulador-field
        id="margem-contribuicao-minima"
        :label="$t('tools.margemContribuicaoMinimaLabel')"
        :value="margemContribuicaoMinima"
        unit="%"
        :formula="$t('tools.margemContribuicaoMinimaFormula')"
        :is-result="true"
      />

      <simulador-field
        id="lucro-projetado"
        :label="$t('tools.lucroProjetadoLabel')"
        :value="lucroProjetado"
        unit="R$"
        unit-position="left"
        :formula="$t('tools.lucroProjetadoFormula')"
        :is-result="true"
      />
    </div>
  </simulador-card>
</template>

<script>
import SimuladorCard from "./SimuladorCard.vue";
import SimuladorField from "./SimuladorField.vue";

export default {
  name: "AnaliseViabilidade",
  components: {
    SimuladorCard,
    SimuladorField
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        margemContribuicao: "0",
        margemContribuicaoMinima: "0",
        lucroProjetado: "0",
        percentualLucro: "0"
      })
    },
    totalContasVariaveis: {
      type: [Number, String],
      default: "0"
    },
    totalContasFixas: {
      type: [Number, String],
      default: "0"
    },
    valorInvestimento: {
      type: [Number, String],
      default: "0"
    },
    faturamentoConservador: {
      type: [Number, String],
      default: 0
    }
  },
  data() {
    return {
      localValue: { ...this.value }
    };
  },
  computed: {
    margemContribuicao: {
      get() {
        return this.localValue.margemContribuicao;
      },
      set(val) {
        this.localValue.margemContribuicao = val;
        this.$emit("input", { ...this.localValue });
      }
    },
    margemContribuicaoMinima: {
      get() {
        return this.localValue.margemContribuicaoMinima;
      },
      set(val) {
        this.localValue.margemContribuicaoMinima = val;
        this.$emit("input", { ...this.localValue });
      }
    },
    lucroProjetado: {
      get() {
        return this.localValue.lucroProjetado;
      },
      set(val) {
        this.localValue.lucroProjetado = val;
        this.$emit("input", { ...this.localValue });
      }
    }
  },
  methods: {
    formatarMoeda(valor) {
      return valor.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    formatarValor(valor) {
      // Converte para número e arredonda para 2 casas decimais
      const numero = parseFloat(valor);
      if (isNaN(numero)) return "0";
      return numero.toFixed(2);
    },
    calcularMargemContribuicao() {
      // Garantir que estamos trabalhando com números
      let faturamento = parseFloat(this.faturamentoConservador) || 0;
      let percentualVariaveis = parseFloat(this.totalContasVariaveis) || 0;

      // Calcular a margem de contribuição (agora é o próprio total de contas variáveis)
      if (percentualVariaveis > 1) {
        // Se o valor for fornecido como percentual (ex: 25 em vez de 0.25)
        percentualVariaveis = percentualVariaveis / 100;
      }

      // Atualizar margemContribuicaoMinima (100% - contas variáveis)
      const margemContribuicaoMinima = 100 - percentualVariaveis * 100;
      this.localValue.margemContribuicaoMinima =
        margemContribuicaoMinima.toFixed(2).replace(".", ",") + "%";

      // Manter a compatibilidade com o código existente
      const margemContribuicao = faturamento * (1 - percentualVariaveis);
      this.localValue.margemContribuicao = margemContribuicao.toFixed(2);

      // Emitir evento de atualização
      this.$emit("input", { ...this.localValue });

      return margemContribuicao;
    },
    calcularLucroProjetado() {
      // Obter valor do faturamento conservador
      let valorFaturamento = 0;

      // Extrair o valor numérico do faturamento conservador
      if (typeof this.faturamentoConservador === "number") {
        valorFaturamento = this.faturamentoConservador;
      } else if (typeof this.faturamentoConservador === "string") {
        // Remover caracteres não numéricos, exceto vírgula e ponto
        const limpo = this.faturamentoConservador.replace(/[^\d,.]/g, "");
        // Substituir vírgula por ponto (padrão JS)
        const formatado = limpo.replace(/\./g, "").replace(",", ".");
        valorFaturamento = parseFloat(formatado) || 0;
      }

      // Definir o percentual de lucro diretamente (convertendo de string para número se necessário)
      let percentualLucro = 0.2; // Valor fixo de 20%

      // Calcular o lucro projetado: faturamento * percentual de lucro
      const lucroProjetado = valorFaturamento * percentualLucro;

      // Formatar o lucro projetado para exibição
      const lucroFormatado = this.formatarMoeda(lucroProjetado);
      this.localValue.lucroProjetado = lucroFormatado;

      // Atualizar percentual de lucro (fixo em 20%)
      this.localValue.percentualLucro = (percentualLucro * 100).toFixed(2);

      // Emitir evento de atualização
      this.$emit("input", { ...this.localValue });

      // Emitir eventos com os valores atualizados
      this.$emit(
        "percentual-lucro-atualizado",
        this.localValue.percentualLucro
      );

      this.$emit("lucro-projetado-atualizado", lucroProjetado);

      return lucroProjetado;
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.localValue = { ...newVal };
      },
      deep: true
    },
    totalContasVariaveis: {
      handler(newVal) {
        this.$nextTick(() => {
          this.calcularMargemContribuicao();
          this.calcularLucroProjetado();
        });
      },
      immediate: true
    },
    totalContasFixas: {
      handler(newVal) {
        this.$nextTick(() => {
          this.calcularLucroProjetado();
        });
      },
      immediate: true
    },
    faturamentoConservador: {
      handler(newVal) {
        this.$nextTick(() => {
          this.calcularMargemContribuicao();
          this.calcularLucroProjetado();
        });
      },
      immediate: true
    },
    valorInvestimento: {
      handler(newVal) {
        // Aqui podemos implementar o cálculo de ROI ou outros indicadores relacionados ao investimento
      },
      immediate: true
    }
  },
  mounted() {
    // Inicializar localValue com o valor da prop
    this.localValue = { ...this.value };

    this.$nextTick(() => {
      // Inicializar os cálculos
      this.calcularMargemContribuicao();
      this.calcularLucroProjetado();

      // Emitir input com o value atualizado
      this.$emit("input", { ...this.localValue });
    });
  }
};
</script>

<style scoped>
.margin-container {
  clear: both;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
}

.margin-container :deep(.field-group) {
  width: 50%;
  padding-right: 15px;
  box-sizing: border-box;
}

@media screen and (max-width: 768px) {
  .margin-container :deep(.field-group) {
    width: 100%;
    padding-right: 0;
  }
}
</style>
